import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../../components/foundations/Icon';
import {
    ARTIFACT_TYPES,
    CHAT_EVENTS,
    FEEDBACK_EVENT_PREFIX,
    FEEDBACK_RATING,
    FEEDBACK_THUMBS,
    INTERACTIVE_SERVICE_BASE_URL,
    trackChatEvent,
} from '../../constants';
import { interactiveServiceFetch } from '../../../services/interactiveServiceFetch';
import { trackMixpanelEvent } from '../../../utils/general';
import FeedbackModal from './FeedbackModal';
import { fetchArtifactContent } from '../../hooks/useArtifactContent';
import { serializeMessageForCopy } from './messageCopy';
import css from './MessageFeedback.css';

const COPY_FLASH_MS = 1500;
const FEEDBACK_ENDPOINT = `${INTERACTIVE_SERVICE_BASE_URL}/feedback`;

const trackFeedbackEvent = (eventName, params) => trackMixpanelEvent(FEEDBACK_EVENT_PREFIX, eventName, params);

// Reloaded artifact blocks arrive with only `content_url` (no inline `data`);
// fetch the stored payload so it can be serialized for copy. Streaming blocks
// (data already present) and blocks without a content_url pass through, as do
// file blocks - their content_url serves raw bytes that nothing here can
// serialize, and a PDF decoded as text would be a pointless multi-MB download.
const hydrateBlock = async block => {
    if (!block || block.data != null || !block.content_url) return block;
    if (block.type === ARTIFACT_TYPES.FILE) return block;
    try {
        return { ...block, data: await fetchArtifactContent(block.content_url) };
    } catch {
        return block;
    }
};

const postFeedback = async payload => {
    const response = await interactiveServiceFetch(FEEDBACK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.status} ${response.statusText}`);
    }
    return response;
};

const retractFeedback = async payload => {
    const response = await interactiveServiceFetch(FEEDBACK_ENDPOINT, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        throw new Error(`Failed to retract feedback: ${response.status} ${response.statusText}`);
    }
    return response;
};

const MessageFeedback = ({
    runId,
    sessionId,
    sessionType,
    contentBlocks,
    onSubmit = postFeedback,
    onRetract = retractFeedback,
    onSubmitted,
    onFailed,
    extraActions = null,
    className,
}) => {
    const [rating, setRating] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReasonKeys, setSelectedReasonKeys] = useState([]);
    const [comment, setComment] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return undefined;
        const timeoutId = setTimeout(() => setCopied(false), COPY_FLASH_MS);
        return () => clearTimeout(timeoutId);
    }, [copied]);

    const submitPayload = useCallback(
        async (finalRating, previousRating) => {
            const isNegative = finalRating === FEEDBACK_RATING.NEGATIVE;
            const payload = {
                runId,
                sessionId,
                sessionType,
                rating: finalRating,
                reasons: isNegative ? selectedReasonKeys : [],
                comment: isNegative ? comment : '',
            };
            try {
                await onSubmit(payload);
                onSubmitted();
                trackFeedbackEvent('Rating Submitted', {
                    rating: finalRating,
                    reasons: payload.reasons,
                    comment: payload.comment,
                    runId,
                    sessionId,
                });
            } catch (err) {
                console.error('Failed to submit feedback:', err);
                setRating(previousRating);
                onFailed();
            }
        },
        [runId, sessionId, sessionType, selectedReasonKeys, comment, onSubmit, onSubmitted, onFailed]
    );

    const retract = useCallback(
        async previousRating => {
            try {
                await onRetract({ runId, sessionId, sessionType });
                trackFeedbackEvent('Rating Retracted', {
                    rating: previousRating,
                    runId,
                    sessionId,
                });
            } catch (err) {
                console.error('Failed to retract feedback:', err);
                setRating(previousRating);
                onFailed?.();
            }
        },
        [runId, sessionId, sessionType, onRetract, onFailed]
    );

    const handleThumbClick = useCallback(
        nextRating => {
            if (rating === nextRating) {
                const previousRating = rating;
                setRating(null);
                setSelectedReasonKeys([]);
                setComment('');
                retract(previousRating);
                return;
            }
            if (nextRating === FEEDBACK_RATING.POSITIVE) {
                const previousRating = rating;
                setRating(FEEDBACK_RATING.POSITIVE);
                submitPayload(FEEDBACK_RATING.POSITIVE, previousRating);
                return;
            }
            setIsModalOpen(true);
        },
        [rating, submitPayload, retract]
    );

    const handleCancel = useCallback(() => {
        setIsModalOpen(false);
        setSelectedReasonKeys([]);
        setComment('');
    }, []);

    const handleModalSubmit = useCallback(() => {
        const previousRating = rating;
        setRating(FEEDBACK_RATING.NEGATIVE);
        setIsModalOpen(false);
        submitPayload(FEEDBACK_RATING.NEGATIVE, previousRating);
    }, [rating, submitPayload]);

    const handleChipToggle = useCallback(reasonKey => {
        setSelectedReasonKeys(prev =>
            prev.includes(reasonKey) ? prev.filter(key => key !== reasonKey) : [...prev, reasonKey]
        );
    }, []);

    const [messageText, setMessageText] = useState('');
    const [isHydrating, setIsHydrating] = useState(false);

    // Resolve any reload blocks (content_url → data) and serialize ahead of the
    // click, so handleCopy can write to the clipboard synchronously within the
    // user gesture (awaiting a fetch there would drop transient activation).
    // isHydrating gates copy until blocks resolve, so we never copy stale or empty text.
    useEffect(() => {
        let cancelled = false;
        setIsHydrating(true);
        Promise.all((contentBlocks || []).map(hydrateBlock)).then(hydrated => {
            if (cancelled) return;
            setMessageText(serializeMessageForCopy(hydrated));
            setIsHydrating(false);
        });
        return () => {
            cancelled = true;
        };
    }, [contentBlocks]);

    const handleCopy = useCallback(async () => {
        if (isHydrating || !messageText) return;
        try {
            await navigator.clipboard.writeText(messageText);
            setCopied(true);
            trackChatEvent(CHAT_EVENTS.COPY_MESSAGE, { runId, sessionId });
        } catch (err) {
            console.error('Failed to copy message text:', err);
        }
    }, [isHydrating, messageText, runId, sessionId]);

    return (
        <div className={classNames(css.feedbackRoot, className)}>
            <div className={css.actionsRow}>
                <div className={css.feedbackRow}>
                    {FEEDBACK_THUMBS.map(thumb => {
                        const isSelected = rating === thumb.rating;
                        if (rating !== null && !isSelected) return null;
                        return (
                            <Icon
                                key={thumb.rating}
                                type={IconTypes.MINGCUTE}
                                name={isSelected ? thumb.filledIcon : thumb.outlinedIcon}
                                size={IconSizes.X_SMALL}
                                variant={isSelected ? thumb.selectedVariant : IconVariants.LIGHT}
                                onClick={() => handleThumbClick(thumb.rating)}
                            />
                        );
                    })}
                    <Icon
                        type={IconTypes.TABLER}
                        name={copied ? 'check' : 'copy'}
                        size={IconSizes.X_SMALL}
                        variant={IconVariants.LIGHT}
                        onClick={handleCopy}
                    />
                </div>
                {extraActions}
            </div>
            <FeedbackModal
                open={isModalOpen}
                selectedReasonKeys={selectedReasonKeys}
                comment={comment}
                onChipToggle={handleChipToggle}
                onCommentChange={setComment}
                onCancel={handleCancel}
                onSubmit={handleModalSubmit}
            />
        </div>
    );
};

MessageFeedback.propTypes = {
    runId: PropTypes.string,
    sessionId: PropTypes.string,
    sessionType: PropTypes.string,
    contentBlocks: PropTypes.array,
    onSubmit: PropTypes.func,
    onRetract: PropTypes.func,
    onSubmitted: PropTypes.func,
    onFailed: PropTypes.func,
    extraActions: PropTypes.node,
    className: PropTypes.string,
};

export default MessageFeedback;
